import {Blesk, ModalProvider} from '@blesk-frontend/ui';
import {QueryClient} from '@tanstack/react-query';
import {ReactQueryDevtools} from '@tanstack/react-query-devtools';
import {createRootRouteWithContext, Link, Outlet, useMatches} from '@tanstack/react-router';
import {lazy, Suspense, useEffect} from 'react';

import {useEvents, useUmamiIdentify, useYandexMetrikaIdentify} from '@/hooks';

const TanStackRouterDevtools =
  process.env.NODE_ENV === 'production'
    ? () => null // Render nothing in production
    : lazy(() =>
        import('@tanstack/router-devtools').then((res) => ({
          default: res.TanStackRouterDevtools,
        })),
      );

export const Route = createRootRouteWithContext<{
  queryClient: QueryClient;
  getTitle?: () => string | Promise<string>;
}>()({
  component: Root,
  errorComponent: ({error}) => {
    // @ts-expect-error
    const status = error.status || null;

    return (
      <div className="flex h-screen w-screen flex-col items-center justify-center gap-4 bg-bg-secondary-hover p-4">
        <div className="translate-x-0 cursor-pointer lg:absolute lg:top-14 lg:left-14">
          <Link to="/">
            <Blesk />
          </Link>
        </div>
        <div className="text-center">
          {[401, 402, 403, 404].includes(status) ? 'Страница не найдена' : 'Произошла ошибка'}
        </div>
        <Link className="text-brand-secondary" to="/">
          Вернуть на главную
        </Link>
      </div>
    );
  },
  notFoundComponent: () => {
    return (
      <div className="flex h-screen w-screen flex-col items-center justify-center gap-4 bg-bg-secondary-hover p-4">
        <div className="translate-x-0 cursor-pointer lg:absolute lg:top-14 lg:left-14">
          <Link to="/">
            <Blesk />
          </Link>
        </div>
        <div className="text-center">Страница не найдена</div>
        <Link className="text-brand-secondary" to="/">
          Вернуть на главную
        </Link>
      </div>
    );
  },
});

function Root() {
  const matches = useMatches();

  useEvents();
  useYandexMetrikaIdentify();
  useUmamiIdentify();

  useEffect(() => {
    const defaultTitle = 'Blesk';
    const breadcrumbPromises = [...matches]
      .reverse()
      .map((match) => {
        if (match.status === 'pending' || !match.context || !('getTitle' in match.context)) {
          return undefined;
        }
        const {context} = match;
        return context.getTitle?.();
      })
      .filter(Boolean);
    void Promise.all(breadcrumbPromises).then((titles) => {
      document.title = [...titles, defaultTitle].join(' - ');
      return titles;
    });
  }, [matches]);

  return (
    <>
      <ReactQueryDevtools buttonPosition="top-left" />
      <Suspense>
        <TanStackRouterDevtools position="bottom-right" />
      </Suspense>
      <ModalProvider>
        <Outlet />
      </ModalProvider>
    </>
  );
}
